// purpose: モバイルR2描画がsim poolを各1回だけ観測し、固定長slot列をfamily/FXへ共有する。

import { POOL_CAPACITY } from '../../constants';
import type { GameWorld } from '../../world/GameWorld';

/**
 * Allocation-free, ascending slot views for one fixed-capacity simulation
 * pool. Only the prefix selected by each count is valid for the current frame.
 */
export interface MobilePoolSnapshot {
  readonly activeSlots: Uint16Array;
  readonly activatedSlots: Uint16Array;
  readonly deactivatedSlots: Uint16Array;
  readonly activeCount: number;
  readonly activatedCount: number;
  readonly deactivatedCount: number;
}

class MutablePoolSnapshot implements MobilePoolSnapshot {
  readonly activeSlots: Uint16Array;
  readonly activatedSlots: Uint16Array;
  readonly deactivatedSlots: Uint16Array;
  private readonly previousActive: Uint8Array;
  activeCount = 0;
  activatedCount = 0;
  deactivatedCount = 0;

  constructor(capacity: number) {
    this.activeSlots = new Uint16Array(capacity);
    this.activatedSlots = new Uint16Array(capacity);
    this.deactivatedSlots = new Uint16Array(capacity);
    this.previousActive = new Uint8Array(capacity);
  }

  observe(active: Uint8Array, sceneReset: boolean): void {
    let activeCount = 0;
    let activatedCount = 0;
    let deactivatedCount = 0;
    for (let slot = 0; slot < this.previousActive.length; slot += 1) {
      const live = active[slot]!;
      const wasLive = this.previousActive[slot]!;
      if (live !== 0) {
        this.activeSlots[activeCount] = slot;
        activeCount += 1;
        if (sceneReset || wasLive === 0) {
          this.activatedSlots[activatedCount] = slot;
          activatedCount += 1;
        }
      }
      if (wasLive !== 0 && (sceneReset || live === 0)) {
        this.deactivatedSlots[deactivatedCount] = slot;
        deactivatedCount += 1;
      }
      this.previousActive[slot] = live;
    }
    this.activeCount = activeCount;
    this.activatedCount = activatedCount;
    this.deactivatedCount = deactivatedCount;
  }

  prepareSceneReset(): void {
    this.activeCount = 0;
    this.activatedCount = 0;
    this.deactivatedCount = 0;
  }
}

/**
 * Renderer-local observation boundary for the four fixed-capacity pools shared
 * by mobile families and game-feel. `sync()` is the only full traversal; all
 * consumers read fixed typed-array prefixes without allocating or rescanning
 * `indices.active`.
 */
export class MobilePoolObserver {
  private readonly mutableEnemies = new MutablePoolSnapshot(POOL_CAPACITY.enemies);
  private readonly mutableProjectiles = new MutablePoolSnapshot(POOL_CAPACITY.projectiles);
  private readonly mutableParticles = new MutablePoolSnapshot(POOL_CAPACITY.particles);
  private readonly mutablePickups = new MutablePoolSnapshot(POOL_CAPACITY.pickups);
  private sceneResetPending = false;
  private sceneResetFrame = false;

  readonly enemies: MobilePoolSnapshot = this.mutableEnemies;
  readonly projectiles: MobilePoolSnapshot = this.mutableProjectiles;
  readonly particles: MobilePoolSnapshot = this.mutableParticles;
  readonly pickups: MobilePoolSnapshot = this.mutablePickups;

  /** True only for the first observation made after `onSceneReset()`. */
  get isSceneResetFrame(): boolean {
    return this.sceneResetFrame;
  }

  sync(world: GameWorld): void {
    const sceneReset = this.sceneResetPending;
    this.mutableEnemies.observe(world.enemies.indices.active, sceneReset);
    this.mutableProjectiles.observe(world.projectiles.indices.active, sceneReset);
    this.mutableParticles.observe(world.particles.indices.active, sceneReset);
    this.mutablePickups.observe(world.pickups.indices.active, sceneReset);
    this.sceneResetFrame = sceneReset;
    this.sceneResetPending = false;
  }

  /**
   * The next observation exposes both sides of the scene boundary: every old
   * live slot is deactivated so families release stale proxies, and every
   * current live slot is activated so edge-driven FX starts from a clean scene.
   */
  onSceneReset(): void {
    this.sceneResetPending = true;
    this.sceneResetFrame = false;
    this.mutableEnemies.prepareSceneReset();
    this.mutableProjectiles.prepareSceneReset();
    this.mutableParticles.prepareSceneReset();
    this.mutablePickups.prepareSceneReset();
  }
}
